/**
 * VariableFieldSelector
 *
 * Component for selecting variables from strategy's variable set.
 * Variables are passed as prop (not loaded from Firestore).
 *
 * - Groups variables by task (global vs task-specific)
 * - Uses rc-tree for tree display with checkboxes
 * - Path format: @variable[{variableId}]
 * - selectAll: true by default (all variables included)
 *
 * CRITICAL: Tree keys are unique per group-variable combination:
 * `var-{groupId}-{variableId}` to avoid rc-tree duplicate key issues.
 *
 * ── Follow-the-task-set model (strategy-intake-feasibility, Task 8) ──────────
 * Two INDEPENDENT concerns, historically conflated in `taskIdFilter`:
 *
 * - VISIBILITY (`taskIdFilter`): which variables are rendered at all. Legacy
 *   consumers keep it; it hides everything outside the filter.
 * - SELECTION (`followSelectedTasks` + `selectedTaskIds`): every variable stays
 *   VISIBLE, and the task-scoped ones are auto-(de)selected with their route.
 *   Groups of unselected routes are marked «route not selected» so the tree is
 *   honest about why nothing under them is ticked.
 *
 * Declaring `followSelectedTasks` (true OR false) opts a consumer into the
 * selection model; `taskIdFilter` should then be left undefined.
 *
 * Manual edits win over the rule (AC-018): a variable the operator unchecks is
 * recorded as a manual exclusion and is NOT re-added when its route (or any
 * other) is toggled. Re-checking it clears the exclusion.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import Tree from 'rc-tree';
import 'rc-tree/assets/index.css';
import type { Key } from 'rc-tree/lib/interface';
import type { FunnelVariable } from '@segmently/schema-models/v2';

import type { VariableFieldSelectorProps } from './types';
import {
  variablesToTreeNodes,
  nodeIdsToPaths,
  pathsToNodeIds,
  countUniqueVariables,
} from './variableToTreeNode';

/** Task ids a variable is scoped to (empty/absent = global). */
function taskIdsOf(variable: FunnelVariable): string[] {
  const ids = (variable as unknown as { taskIds?: string[] }).taskIds;
  return Array.isArray(ids) ? ids : [];
}

/** Order-independent comparison — the parent may re-route paths before echoing them back. */
function sameIdSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const seen = new Set(a);
  return b.every((item) => seen.has(item));
}

function pathToId(path: string): string | null {
  return path.match(/^@variable\[([^\]]+)\]/)?.[1] ?? null;
}

export const VariableFieldSelector: React.FC<VariableFieldSelectorProps> = ({
  variables,
  tasks,
  value,
  onChange,
  height = 400,
  selectAll = true,
  onHydratedDataChange,
  taskIdFilter,
  followSelectedTasks,
  selectedTaskIds,
  showSelectionSummary = true,
}) => {
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);

  /** The consumer opted into the follow model (visibility filtering is then off). */
  const followModel = followSelectedTasks !== undefined;
  const followEnabled = followSelectedTasks === true;

  // Stable primitive for effect deps — the caller rebuilds the array each render.
  const selectedTaskIdsKey = (selectedTaskIds ?? []).join(',');

  // Phase F: when taskIdFilter is set, show only global variables (no taskIds) + variables whose
  // taskIds intersect the selected tasks. `undefined` = show all (back-compat).
  const visibleVariables = useMemo<Record<string, FunnelVariable>>(() => {
    if (!taskIdFilter) return variables;
    const allow = new Set(taskIdFilter);
    const out: Record<string, FunnelVariable> = {};
    for (const [id, v] of Object.entries(variables)) {
      const tIds = taskIdsOf(v);
      if (tIds.length === 0 || tIds.some((t) => allow.has(t))) out[id] = v;
    }
    return out;
  }, [variables, taskIdFilter]);

  /** Unique visible variables in a stable order — the order emitted paths keep. */
  const orderedVariables = useMemo<FunnelVariable[]>(() => {
    const seen = new Set<string>();
    const out: FunnelVariable[] = [];
    for (const variable of Object.values(visibleVariables)) {
      if (!variable.id || seen.has(variable.id)) continue;
      seen.add(variable.id);
      out.push(variable);
    }
    return out;
  }, [visibleVariables]);

  // Build tree data from the visible variables. Under the follow model the tree
  // keeps EVERY route's group and marks the ones whose route is not selected.
  const treeData = useMemo(
    () =>
      variablesToTreeNodes(
        visibleVariables,
        tasks,
        followModel ? new Set(selectedTaskIds ?? []) : undefined,
      ),
    // selectedTaskIdsKey stands in for the array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleVariables, tasks, followModel, selectedTaskIdsKey],
  );

  // Count unique (visible) variables
  const varCount = useMemo(() => countUniqueVariables(visibleVariables), [visibleVariables]);

  // Count selected unique variables from checkedKeys (the rendered plane).
  const selectedCount = useMemo(
    () => new Set(nodeIdsToPaths(checkedKeys, variables).map(pathToId)).size,
    [checkedKeys, variables],
  );

  /** Selected variable ids — the single plane every rule reads and writes. */
  const selectedIdsRef = useRef<string[]>([]);
  /** Ids the operator unchecked by hand; the follow rule never re-adds them. */
  const manualExclusionsRef = useRef<Set<string>>(new Set());
  /** Last paths handed to `onChange`, to recognise the parent's echo. */
  const lastEmittedRef = useRef<string[] | null>(null);
  /** A (re)initialisation has run for the currently loaded variable set. */
  const initializedRef = useRef(false);
  /** Preselection already ran for the current EMPTY external value. */
  const preselectedRef = useRef(false);
  /** Task set / switch state observed by the follow effect on its previous run. */
  const prevTaskIdsRef = useRef<string[] | null>(null);
  const prevFollowRef = useRef<boolean | undefined>(undefined);

  const emitHydrated = useCallback(
    (ids: readonly string[]) => {
      if (!onHydratedDataChange) return;
      const wanted = new Set(ids);
      onHydratedDataChange(Object.values(variables).filter((v) => wanted.has(v.id)));
    },
    [variables, onHydratedDataChange],
  );

  /**
   * Writes a selection: tree keys, emitted paths and hydrated data in one place,
   * so the picker plane and the parent's plane can never drift apart.
   */
  const applySelection = useCallback(
    (ids: ReadonlySet<string>, { force = false }: { force?: boolean } = {}) => {
      const nextIds = orderedVariables.map((v) => v.id).filter((id) => ids.has(id));
      if (!force && sameIdSet(nextIds, selectedIdsRef.current)) return;
      selectedIdsRef.current = nextIds;
      const paths = nextIds.map((id) => `@variable[${id}]`);
      setCheckedKeys(pathsToNodeIds(paths, visibleVariables));
      lastEmittedRef.current = paths;
      onChange(paths);
      emitHydrated(nextIds);
    },
    [orderedVariables, visibleVariables, onChange, emitHydrated],
  );

  /** Ids of the visible variables scoped to at least one of `taskIds`. */
  const variableIdsForTasks = useCallback(
    (taskIds: readonly string[]): string[] => {
      if (taskIds.length === 0) return [];
      const allow = new Set(taskIds);
      return orderedVariables
        .filter((v) => taskIdsOf(v).some((t) => allow.has(t)))
        .map((v) => v.id);
    },
    [orderedVariables],
  );

  /**
   * Deterministic (re)initialisation.
   *
   * Runs on the first non-empty variables load AND whenever `value` is replaced
   * from OUTSIDE — reopening the dialog resets the orchestrator selection to
   * `[]`, and the old one-shot `initializedRef` made that reopen preselect
   * nothing at all. Our own emissions come back as an echo and are ignored, so
   * an operator who unchecks everything is not overruled.
   */
  useEffect(() => {
    if (varCount === 0) {
      initializedRef.current = false;
      preselectedRef.current = false;
      manualExclusionsRef.current.clear();
      selectedIdsRef.current = [];
      lastEmittedRef.current = null;
      return;
    }

    const echoed = lastEmittedRef.current !== null && sameIdSet(lastEmittedRef.current, value);
    if (echoed) {
      initializedRef.current = true;
      // A non-empty selection re-arms preselection for the NEXT external reset.
      if (value.length > 0) preselectedRef.current = false;
      return;
    }

    if (value.length > 0) {
      // External (saved) selection — adopt it verbatim, never overwrite it.
      const ids = value.map(pathToId).filter((id): id is string => Boolean(id));
      selectedIdsRef.current = ids;
      lastEmittedRef.current = null;
      setCheckedKeys(pathsToNodeIds(value, visibleVariables));
      initializedRef.current = true;
      preselectedRef.current = false;
      return;
    }

    // External EMPTY value = a fresh open. Preselect once per reset.
    initializedRef.current = true;
    if (preselectedRef.current) return;
    preselectedRef.current = true;
    if (!selectAll) return;

    const routes = new Set(selectedTaskIds ?? []);
    const preselected = new Set<string>();
    for (const variable of orderedVariables) {
      const scopes = taskIdsOf(variable);
      if (scopes.length === 0) {
        preselected.add(variable.id);
        continue;
      }
      if (!followModel) {
        preselected.add(variable.id);
        continue;
      }
      // Follow OFF preselects the globals only (design brief: «variables drop
      // to the 5 global»); follow ON adds the routes that are selected.
      if (!followEnabled) continue;
      if (scopes.some((t) => routes.has(t)) && !manualExclusionsRef.current.has(variable.id)) {
        preselected.add(variable.id);
      }
    }
    applySelection(preselected, { force: true });
    // `selectedTaskIdsKey` stands in for the array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    value,
    varCount,
    visibleVariables,
    orderedVariables,
    selectAll,
    followModel,
    followEnabled,
    selectedTaskIdsKey,
    applySelection,
  ]);

  /**
   * The follow rule: the task set (and the switch itself) move the selection.
   * Declared AFTER the initialisation effect so a first mount always preselects
   * before any transition is evaluated.
   */
  useEffect(() => {
    if (!followModel) return;

    const current = selectedTaskIds ?? [];
    const prevTaskIds = prevTaskIdsRef.current;
    const prevFollow = prevFollowRef.current;

    if (!initializedRef.current) {
      // Preselection owns the first pass — forget this observation entirely.
      prevTaskIdsRef.current = null;
      prevFollowRef.current = undefined;
      return;
    }

    prevTaskIdsRef.current = current;
    prevFollowRef.current = followSelectedTasks;
    if (prevTaskIds === null || prevFollow === undefined) return;

    const followChanged = prevFollow !== followSelectedTasks;
    const tasksChanged = !sameIdSet(prevTaskIds, current);
    if (!followChanged && !tasksChanged) return;

    const next = new Set(selectedIdsRef.current);

    if (!followEnabled) {
      // AC-017: while the switch is OFF, a route change never moves anything.
      if (!followChanged) return;
      for (const variable of orderedVariables) {
        if (taskIdsOf(variable).length > 0) next.delete(variable.id);
      }
      applySelection(next);
      return;
    }

    if (followChanged) {
      // Switch turned ON — adopt the route set that is selected right now.
      for (const id of variableIdsForTasks(current)) {
        if (!manualExclusionsRef.current.has(id)) next.add(id);
      }
      applySelection(next);
      return;
    }

    const currentSet = new Set(current);
    const added = current.filter((id) => !prevTaskIds.includes(id));
    const removed = prevTaskIds.filter((id) => !currentSet.has(id));

    for (const id of variableIdsForTasks(added)) {
      if (!manualExclusionsRef.current.has(id)) next.add(id);
    }
    for (const id of variableIdsForTasks(removed)) {
      // A variable shared with a still-selected route stays in.
      const variable = visibleVariables[id];
      if (variable && taskIdsOf(variable).some((t) => currentSet.has(t))) continue;
      next.delete(id);
    }
    applySelection(next);
    // `selectedTaskIdsKey` stands in for the array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedTaskIdsKey,
    followSelectedTasks,
    followModel,
    followEnabled,
    orderedVariables,
    visibleVariables,
    variableIdsForTasks,
    applySelection,
  ]);

  // Phase F (legacy visibility model only): when the task filter changes, prune
  // selected paths + checkedKeys to the visible variables.
  useEffect(() => {
    if (!taskIdFilter) return;
    const visibleIds = new Set(Object.keys(visibleVariables));
    const prunedValue = value.filter((p) => {
      const id = pathToId(p);
      return !id || visibleIds.has(id);
    });
    if (prunedValue.length !== value.length) onChange(prunedValue);
    setCheckedKeys((prev) =>
      prev.filter((k) => !k.startsWith('var-') || Array.from(visibleIds).some((id) => k.endsWith(`-${id}`))),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskIdFilter, visibleVariables]);

  // Handle check/uncheck
  const handleCheck = useCallback(
    (checked: Key[] | { checked: Key[]; halfChecked: Key[] }) => {
      const keys = Array.isArray(checked) ? checked : checked.checked;
      const nextIds = new Set(
        nodeIdsToPaths(keys.map(String), variables)
          .map(pathToId)
          .filter((id): id is string => Boolean(id)),
      );

      // AC-018: remember what the operator turned off BY HAND — the follow rule
      // must not undo it on the next route toggle.
      for (const id of selectedIdsRef.current) {
        if (!nextIds.has(id)) manualExclusionsRef.current.add(id);
      }
      for (const id of nextIds) {
        manualExclusionsRef.current.delete(id);
      }

      applySelection(nextIds, { force: true });
    },
    [variables, applySelection],
  );

  if (varCount === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No variables defined on this strategy.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height, overflow: 'auto' }}>
      {showSelectionSummary && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 1, mb: 0.5, display: 'block' }}>
          {selectedCount} of {varCount} variables selected
        </Typography>
      )}
      <Tree
        checkable
        checkStrictly={false}
        checkedKeys={checkedKeys}
        onCheck={handleCheck}
        treeData={treeData}
        defaultExpandAll
        selectable={false}
      />
    </Box>
  );
};

export default VariableFieldSelector;
